import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAttendanceDays,
  fetchAttendanceSummary,
  fetchMembers,
  fetchWorkers,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { todayInZone } from '../lib/attendance-session';
import { buildAttendanceRoster, initialsOf } from '../lib/attendance-roster';
import { formatDateInZone, formatTimeInZone, formatWorkMode } from '../lib/format';
import { ErrorCard, LoadingState } from '../states';

/**
 * Dashboard absensi — satu layar untuk memutuskan.
 *
 * Angka ditampilkan APA ADANYA dari `summary`. UI tidak menghitung ulang status kehadiran:
 * server sudah memutuskannya memakai `policy_snapshot` yang berlaku saat event terjadi, dan
 * perhitungan kedua di klien akan menyimpang diam-diam begitu kebijakan berubah.
 *
 * Seluruh angka scope-aware — server yang menentukan cakupannya dari peran pemanggil, bukan
 * parameter dari layar ini. Membuka URL-nya tanpa wewenang tidak menghasilkan angka orang lain.
 */
export function AttendanceDashboardPage(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  // Tanggal bawaan memakai timezone ORGANISASI, bukan timezone browser — manajer yang membuka
  // dari zona lain tetap melihat hari kerja organisasinya.
  const [date, setDate] = useState(() => todayInZone(new Date(), timezone));

  /*
   * Empat panggilan, satu layar. Angka DAN nama diambil bersamaan supaya keduanya menggambarkan
   * tanggal yang sama — memuatnya terpisah membuka celah waktu kecil yang, tepat saat seseorang
   * check-in, menghasilkan "Hadir 3" di atas daftar berisi dua nama.
   *
   * Nama tidak disimpan di Workforce — profil pekerja hanya memuat atribut operasional. Jadi
   * roster HBS ikut diambil, dan digabungkan lewat `userId`; pola yang sama dengan halaman
   * Karyawan.
   */
  const { state, reload } = useAsync(async () => {
    const [summary, workers, members, days] = await Promise.all([
      fetchAttendanceSummary(session.auth, date),
      fetchWorkers(session.auth),
      fetchMembers(session.auth),
      fetchAttendanceDays(session.auth, date, date),
    ]);
    return { summary, roster: buildAttendanceRoster(workers, members, days, date) };
  }, [session.auth, date]);

  return (
    <>
      <section className="card">
        <h2>Pantauan absensi</h2>
        <label htmlFor="date">Tanggal</label>
        <input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <p className="muted small">
          {formatDateInZone(`${date}T00:00:00.000Z`, 'UTC')} · zona {timezone}
        </p>
      </section>

      {state.status === 'loading' ? <LoadingState label="Memuat ringkasan…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' ? (
        <>
          <section className="card">
            <h3>Kehadiran</h3>
            <div className="board">
              <Cell label="Hadir" value={state.data.summary.present} tone="green" />
              <Cell label="Terlambat" value={state.data.summary.late} tone="amber" />
              <Cell label="Belum absen masuk" value={state.data.summary.notCheckedIn} tone="pink" />
              <Cell label="Belum lengkap" value={state.data.summary.incomplete} tone="purple" />
              <Cell label="Kerja lapangan" value={state.data.summary.fieldWork} tone="blue" />
              <Cell label="Tidak hadir" value={state.data.summary.absent} />
            </div>
            <p className="muted small">
              Angka ini ditetapkan server memakai kebijakan yang berlaku saat itu. Perubahan
              kebijakan setelahnya tidak mengubah catatan hari ini.
            </p>
          </section>

          {/*
            Daftar BERNAMA, tepat di bawah angkanya.
            Angka menjawab "berapa"; hanya nama yang menjawab "siapa" — dan jam sembilan pagi,
            pertanyaan yang menuntut tindakan adalah yang kedua. "Belum absen masuk" sengaja berada
            di urutan teratas karena ia satu-satunya seksi yang bisa ditindaklanjuti.
          */}
          <section className="card">
            <h3>Siapa saja</h3>
            {state.data.roster.length === 0 ? (
              <p className="muted small">
                Belum ada pekerja aktif pada organisasi ini, jadi tidak ada yang dapat didaftar.
              </p>
            ) : (
              state.data.roster.map((seksi) => (
                <div key={seksi.key} className="roster-group">
                  <div className="row space-between roster-head">
                    <strong>{seksi.label}</strong>
                    <span className="muted small">{seksi.rows.length}</span>
                  </div>
                  <ul className="list">
                    {seksi.rows.map((baris) => (
                      <li key={baris.userId} className="list-item">
                        {/*
                          Seluruh baris menjadi TAUTAN ke detail hari orang itu (23 Agu 2026).
                          Sebelumnya papan ini jalan buntu: ia menyebut siapa yang terlambat, lalu
                          tidak menawarkan satu pun cara melihat MENGAPA — bukan petanya, bukan
                          akurasinya, bukan alasan di luar radius yang wajib ditulis kurir.

                          Tautan dipasang untuk SEMUA baris, tidak disaring peran di sini. Cakupan
                          ditegakkan server (`assertCanAccessUser`), dan kurir yang membuka papan
                          ini memang hanya melihat dirinya sendiri — jadi tautannya menuju harinya
                          sendiri, yang sah.
                        */}
                        <Link
                          className="row space-between"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                          to={`/attendance/history/${encodeURIComponent(baris.userId)}/${encodeURIComponent(date)}`}
                        >
                          <span className="row" style={{ alignItems: 'center', gap: '0.6rem' }}>
                            {/* Lingkaran inisial: jangkar visual, BUKAN pengganti nama. */}
                            <span className="avatar" aria-hidden="true">
                              {initialsOf(baris.name)}
                            </span>
                            {baris.name}
                          </span>
                          <span className="muted small">
                            {baris.firstCheckInAt !== null
                              ? formatTimeInZone(baris.firstCheckInAt, timezone)
                              : '—'}
                            {baris.lateMinutes > 0 ? ` · telat ${baris.lateMinutes} mnt` : ''}
                            {/*
                              Mode kerja HANYA disebut saat bukan default "Di kantor" — menyebut
                              WFO di setiap baris hanya kebisingan berulang untuk kasus yang
                              sudah diasumsikan semua orang (review 29 Sep 2026, laporan Owner).
                            */}
                            {baris.workMode !== null && baris.workMode !== 'WFO'
                              ? ` · ${formatWorkMode(baris.workMode)}`
                              : ''}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>

          {/*
            Indikator tidak terverifikasi DITONJOLKAN, bukan disembunyikan di balik detail.
            Angka "hadir" yang terlihat rapi padahal separuhnya tanpa GPS adalah kegagalan
            produk — manajer yang menandatangani rekap berhak tahu sebelum menandatanganinya.
          */}
          {state.data.summary.unverifiedLocationCount > 0 ? (
            <section className="card" role="status">
              <p className="warning">
                {state.data.summary.unverifiedLocationCount} event tanpa verifikasi lokasi penuh
                hari ini.
              </p>
              <p className="muted small">
                Kehadirannya tetap tercatat, tetapi lokasinya tidak terkonfirmasi. Periksa sebelum
                memakai rekap ini sebagai dasar keputusan.
              </p>
            </section>
          ) : (
            <section className="card">
              <p className="muted small">Seluruh event hari ini terverifikasi lokasinya.</p>
            </section>
          )}

          <section className="card">
            <div className="row space-between">
              <h3>Koreksi menunggu</h3>
              <strong>{state.data.summary.pendingCorrectionCount}</strong>
            </div>
            {state.data.summary.pendingCorrectionCount > 0 ? (
              <Link className="button-link" to="/attendance/corrections">
                Buka antrean approval
              </Link>
            ) : (
              <p className="muted small">Tidak ada pengajuan yang menunggu keputusan.</p>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}

/*
 * Sel angka, bentuk dari desain 22 Agu: bidang pastel, angka besar, label kecil.
 *
 * Warnanya BUKAN dekorasi dan bukan pula satu-satunya penanda — labelnya selalu tertulis penuh
 * di bawah angkanya. Gunanya membuat kelompok yang menuntut tindakan ("Belum check-in") dapat
 * ditemukan tanpa membaca keenam sel satu per satu. Kelimanya sudah dihitung kontrasnya
 * terhadap ink: 13,03–15,34:1.
 */
function Cell({
  label,
  value,
  tone,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone?: 'blue' | 'amber' | 'green' | 'purple' | 'pink';
}): ReactNode {
  return (
    <div className={tone ? `board-cell tone-${tone}` : 'board-cell'}>
      <span className="board-count">{value}</span>
      <span className="muted small">{label}</span>
    </div>
  );
}
